import React, { ReactElement, useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import {
  StyledBlueButton,
  StyledRedButton,
  StyledButton,
} from '../../Button/Button';
import CloseIcon from './x_close_icon.svg';
import { TextInput } from 'carbon-components-react';
import { Loader } from '../../Loader/Loader';
import InfoSVG from './circled_i_icon.svg';
import { OpenNow } from '../../Icons/OpenNow';

type Props = {
  headline: string;
  confirmButtonText: string;
  updateCheckInCallback: ({
    daysToCheckIn,
  }: {
    daysToCheckIn: number;
  }) => Promise<void>;
  dismissButtonText: string;
  loading: boolean;
  data: { daysToCheckIn: number };
  dismissButtonCallback: () => void;
  dismissToPath?: string | null;
  confirmButtonColor: 'blue' | 'red';
};

export const dateInDaysFromNow = (dayCount: number): string => {
  const now = new Date();
  return new Date(now.setDate(now.getDate() + dayCount)).toLocaleDateString();
};

export const dayDifferenceBetweenDates = (start: Date, end: Date): number => {
  const utc1 = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utc2 = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  return Math.floor((utc2 - utc1) / 1000 / 60 / 60 / 24);
};

export const EditDaysToCheckIn = ({
  headline,
  data,
  loading,
  confirmButtonText,
  confirmButtonColor,
  updateCheckInCallback,
  dismissToPath,
  dismissButtonText,
  dismissButtonCallback,
}: Props): ReactElement | null => {
  const dismissDialog: React.MouseEventHandler<HTMLImageElement> = (e) => {
    e.preventDefault();
    dismissButtonCallback();
  };

  const [daysToCheckInValue, setDaysToCheckInValue] = useState(
    data.daysToCheckIn
  );
  const [checkInDate, setCheckInDate] = useState(
    dateInDaysFromNow(data.daysToCheckIn)
  );

  const confirmDialog: React.MouseEventHandler<HTMLImageElement> = async (
    e
  ) => {
    e.preventDefault();
    updateCheckInCallback({ daysToCheckIn: daysToCheckInValue });
  };
  return (
    <ConfirmationTakeover>
      <ConfirmationWrapper>
        {loading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <>
            <CloseIconImg
              data-testid={'dismiss-x-icon'}
              onClick={dismissDialog}
              src={CloseIcon}
              alt="Close this dialog"
            ></CloseIconImg>
            <Heading>{headline}</Heading>
            <SelectRow>
              <SelectGroup>
                <StyledNumberInput
                  invalidText={`invalid`}
                  data-testid={'number-input'}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    const newDaysToCheckInVal = e.target.value
                      ? parseInt(parseFloat(e.target.value).toFixed(0))
                      : NaN;
                    setDaysToCheckInValue(newDaysToCheckInVal);
                    setCheckInDate(dateInDaysFromNow(newDaysToCheckInVal));
                  }}
                  labelText="Next check-in (days)"
                  disabled={false}
                  type="number"
                  id={`Edit Cycle Number`}
                  size={undefined}
                  invalid={false}
                  min={0}
                  value={daysToCheckInValue as number}
                />
              </SelectGroup>
              <CheckInTiming>
                {daysToCheckInValue >= 1 && (
                  <DateDisplay>{checkInDate}</DateDisplay>
                )}
                {daysToCheckInValue < 1 && <OpenNow />}
              </CheckInTiming>
            </SelectRow>
            <InfoBox>
              <InfoIconImg src={InfoSVG} alt={headline}></InfoIconImg>
              {'Enter 0 to open check-in now'}
            </InfoBox>
            <ButtonWrapper>
              <ConfirmButton
                confirmButtonColor={confirmButtonColor}
                data-testid={'confirm-button'}
                disabled={loading || daysToCheckInValue < 0}
                onClick={confirmDialog}
                kind="secondary"
                size="default"
                tabIndex={0}
                type="button"
              >
                {confirmButtonText}
              </ConfirmButton>
              <DismissButton
                data-testid={'dismiss-button'}
                onClick={dismissDialog}
                kind="primary"
                size="default"
                tabIndex={0}
                type="button"
                href={dismissToPath}
              >
                {dismissButtonText}
              </DismissButton>
            </ButtonWrapper>
          </>
        )}
      </ConfirmationWrapper>
    </ConfirmationTakeover>
  );
};

export const StyledConfirmButton = styled((props) => (
  <StyledBlueButton {...props} />
))`
  margin: 0;
  font-weight: bold;
  width: 100%;
  margin: 10px 0px;
`;

const StyledNumberInput = styled((props) => <TextInput {...props} />)`
  input[type='number'] {
    color: ${(props): string => {
      if (props.invalid) return props.theme.warningRed;
      return props.isDirty ? props.theme.inspireCobalt : 'inherit';
    }};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const Padder = styled.div`
  margin-bottom: ${(props): string => props.theme.contentMarginBottom};
`;

export const ButtonPadder = styled.div`
  margin-bottom: 10px;
`;

const Heading = styled.h2`
  font-weight: normal;
  font-size: ${(props): string => props.theme.l};
  margin: 50px 0px;
`;

// We use z-index to make sure the takeover window sits above any other window content
const ConfirmationTakeover = styled.div`
  display: flex;
  z-index: ${(props): string => props.theme.confirmationTakeover};
  justify-content: center;
  align-items: center;
  background: rgba(57, 57, 57, 0.3);
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  position: fixed;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const ConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
  padding: 20px 50px;
  border: 1px solid ${(props): string => props.theme.grey};
  border-radius: 15px;
  background: ${(props): string => props.theme.white};
  height: 430px;
  width: 422px;
  position: relative;
`;

const ConfirmButton = styled(({ confirmButtonColor, ...rest }) => {
  type Options = {
    blue: JSX.Element;
    red: JSX.Element;
  };

  const options: Options = {
    blue: <StyledBlueButton {...rest} />,
    red: <StyledRedButton {...rest} />,
  };

  return (
    options[confirmButtonColor as keyof Options] || (
      <StyledBlueButton {...rest} />
    )
  );
})`
  width: 100%;
  height: 48px;
`;

const DismissButton = styled((props) => <StyledButton {...props} />)`
  width: 100%;
  height: 48px;
  margin: 10px 0px 50px 0px;
`;

const CloseIconImg = styled.img`
  position: absolute;
  right: 20px;
  cursor: pointer;
`;

const InfoBox = styled.div`
  display: flex;
  margin-bottom: 1em;
`;

const InfoIconImg = styled.img`
  margin-right: 10px;
  width: 14px;
`;

const SelectRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const SelectGroup = styled.div`
  display: flex;
  margin-bottom: 1em;
  width: 50%;
`;

const CheckInTiming = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1em;
`;

const DateDisplay = styled.p`
  font-family: monospace;
  font-size: 14px;
  margin-top: 6px;
`;
